import {
  BaseEventData,
  TestDriveEventData,
  trackPxValuation,
  trackFinanceQuote,
  trackFinanceApplication,
  trackReservationInitiated,
  trackReservationSubmitted,
  trackTestDriveBooked,
  trackCallbackBooked,
  trackEmailBooked,
  trackJourneyDetailsDisplayed,
  BookingEventData,
  VehicleEventData
} from './gtmTracking';
import {
  ChatEventDTOEventTypeEnum,
  WidgetChatResponseWidgetChatEventsInner,
  BookedTestDriveChatEventDTO,
  BookedCallBackChatEventDTO,
  ReturnedFinanceQuoteChatEventDTO,
  ReturnedStripeFormChatEventDTO,
  ReservedVehicleChatEventDTO,
  ReturnedFinanceIllustrationChatEventDTO
} from '../api/generated';


type ChatEventProcessor = (
  event: WidgetChatResponseWidgetChatEventsInner,
  baseData: BaseEventData,
  websiteLocation?: string,
) => void;

const processReturnedVehicleValuation: ChatEventProcessor = (event, baseData, websiteLocation) => {
  trackPxValuation(baseData, websiteLocation);
};

const processReturnedFinanceQuote: ChatEventProcessor = (event, baseData, websiteLocation) => {
  const financeQuoteEvent = event as ReturnedFinanceQuoteChatEventDTO;
  const vehicleData: VehicleEventData = {
    vin: financeQuoteEvent.finance_vin,
  };
  trackFinanceQuote(baseData, vehicleData, websiteLocation);
};

const processReturnedFinanceIllustration: ChatEventProcessor = (event, baseData, websiteLocation) => {
  const financeIllustrationEvent = event as ReturnedFinanceIllustrationChatEventDTO;
  const vehicleData: VehicleEventData = {
    vin: financeIllustrationEvent.finance_vin,
  };
  trackFinanceQuote(baseData, vehicleData, websiteLocation);
};

const processReturnedFinanceApplication: ChatEventProcessor = (event, baseData, websiteLocation) => {
  trackFinanceApplication(baseData, websiteLocation);
};

const processReturnedStripeForm: ChatEventProcessor = (event, baseData, websiteLocation) => {
  const stripeFormEvent = event as ReturnedStripeFormChatEventDTO;
  const vehicleData: VehicleEventData = {
    vin: stripeFormEvent.preferred_reservation_vin,
    vrm: stripeFormEvent.preferred_reservation_vrm,
  };
  trackReservationInitiated(baseData, vehicleData, websiteLocation);
};

const processReservedVehicle: ChatEventProcessor = (event, baseData, websiteLocation) => {
  const reservedVehicleEvent = event as ReservedVehicleChatEventDTO;
  const vehicleData: VehicleEventData = {
    vin: reservedVehicleEvent.reserved_vin,
    vrm: reservedVehicleEvent.reserved_vrm,
  };
  trackReservationSubmitted(baseData, vehicleData, websiteLocation);
};

const processBookedTestDrive: ChatEventProcessor = (event, baseData, websiteLocation) => {
  const testDriveEvent = event as BookedTestDriveChatEventDTO;
  const testDriveEventData: TestDriveEventData = {
    bookingDateTime: testDriveEvent.booking_datetime,
    bookingVrm: testDriveEvent.test_drive_vrm,
    bookingVin: testDriveEvent.test_drive_vin,
  };
  trackTestDriveBooked(baseData, testDriveEventData, websiteLocation);
};

const processBookedCallBack: ChatEventProcessor = (event, baseData, websiteLocation) => {
  const callbackEvent = event as BookedCallBackChatEventDTO;
  const callbackEventData: BookingEventData = {
    bookingDateTime: callbackEvent.booking_datetime
  };
  trackCallbackBooked(baseData, callbackEventData, websiteLocation);
};

const processBookedEmailFollowUp: ChatEventProcessor = (event, baseData, websiteLocation) => {
  trackEmailBooked(baseData, websiteLocation);
};

const processReturnedJourneyDetails: ChatEventProcessor = (event, baseData, websiteLocation) => {
  trackJourneyDetailsDisplayed(baseData, websiteLocation);
};

const EVENT_PROCESSORS: Partial<Record<ChatEventDTOEventTypeEnum, ChatEventProcessor>> = {
  [ChatEventDTOEventTypeEnum.ReturnedVehicleValuation]: processReturnedVehicleValuation,
  [ChatEventDTOEventTypeEnum.ReturnedFinanceQuote]: processReturnedFinanceQuote,
  [ChatEventDTOEventTypeEnum.ReturnedFinanceIllustration]: processReturnedFinanceIllustration,
  [ChatEventDTOEventTypeEnum.ReturnedFinanceApplication]: processReturnedFinanceApplication,
  [ChatEventDTOEventTypeEnum.ReturnedStripeForm]: processReturnedStripeForm,
  [ChatEventDTOEventTypeEnum.ReservedVehicle]: processReservedVehicle,
  [ChatEventDTOEventTypeEnum.BookedTestDrive]: processBookedTestDrive,
  [ChatEventDTOEventTypeEnum.BookedCallBack]: processBookedCallBack,
  [ChatEventDTOEventTypeEnum.BookedEmailFollowUp]: processBookedEmailFollowUp,
  [ChatEventDTOEventTypeEnum.ReturnedJourneyDetails]: processReturnedJourneyDetails,
};

export const processChatEvents = (
  events: WidgetChatResponseWidgetChatEventsInner[],
  baseData: BaseEventData,
  websiteLocation?: string,
): void => {
  if (!events || events.length === 0) {
    return;
  }

  events.forEach(event => {
    if (!event.event_type) {
      console.warn('Chat event missing event_type:', event);
      return;
    }

    const processor = EVENT_PROCESSORS[event.event_type];
    if (processor) {
      try {
        processor(event, baseData, websiteLocation);
      } catch (error) {
        console.error(`Error processing chat event ${event.event_type}:`, error);
      }
    }
  });
};
